/**
 * XSS Sanitization Utility
 * 
 * Provides secure HTML sanitization using DOMPurify to prevent XSS attacks.
 * All v-html and innerHTML usage should pass through these sanitization functions.
 */

import DOMPurify from 'dompurify';
import { logDebug, logWarn } from '@/utils/secureLogger';

/**
 * DOMPurify configuration for educational content
 * Allows common formatting tags while blocking dangerous elements
 */
const SANITIZE_CONFIG = {
  // Allowed HTML tags for educational content
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'span', 'div', 'strong', 'em', 'b', 'i', 'u', 's', 'strike',
    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Lists
    'ul', 'ol', 'li',
    // Tables
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    // Links and images (with restrictions)
    'a', 'img',
    // Code and pre-formatted text
    'code', 'pre', 'kbd', 'samp', 'var',
    // Blockquotes and citations
    'blockquote', 'cite', 'q',
    // Subscript and superscript (useful for math/chemistry)
    'sub', 'sup',
    // Definition lists
    'dl', 'dt', 'dd',
    // Horizontal rule
    'hr',
    // Mark (highlighting)
    'mark',
    // Small text
    'small',
  ],
  
  // Allowed attributes
  ALLOWED_ATTR: [
    'class', 'id', 'style',
    'href', 'target', 'rel',
    'src', 'alt', 'title', 'width', 'height',
    'colspan', 'rowspan',
    'data-*', // Allow data attributes for Vue
  ],
  
  // Allow only http/https/mailto links
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  
  // Keep safe HTML entities
  KEEP_CONTENT: true,
  
  // Return a DOM element instead of string
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  
  // Enforce target="_blank" for external links
  ADD_ATTR: ['target'],
};

/**
 * Strict configuration for user-generated content
 * More restrictive than standard config
 */
const STRICT_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3',
    'code', 'pre',
    'blockquote',
  ],
  ALLOWED_ATTR: ['class'],
  KEEP_CONTENT: true,
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * 
 * @param html - The HTML string to sanitize
 * @param strict - If true, uses stricter sanitization rules
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string, strict: boolean = false): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    const config = strict ? STRICT_SANITIZE_CONFIG : SANITIZE_CONFIG;
    const sanitized = DOMPurify.sanitize(html, config);
    
    // Log if content was modified (potential XSS attempt)
    if (sanitized !== html) {
      logWarn('HTML content was sanitized (potential XSS detected)', {
        originalLength: html.length,
        sanitizedLength: sanitized.length,
        removed: html.length - sanitized.length,
      });
    } else {
      logDebug('HTML content passed sanitization', {
        length: html.length,
      });
    }
    
    return sanitized;
  } catch (error) {
    logWarn('Error during HTML sanitization', error);
    // On error, return empty string for safety
    return '';
  }
}

/**
 * Sanitize HTML attributes (for use in Vue bindings)
 * 
 * @param attr - The attribute value to sanitize
 * @returns Sanitized attribute value
 */
export function sanitizeAttribute(attr: string): string {
  if (!attr || typeof attr !== 'string') {
    return '';
  }

  try {
    // Remove any script-like content from attributes
    const sanitized = DOMPurify.sanitize(attr, {
      ALLOWED_TAGS: [],
      KEEP_CONTENT: true,
    });
    
    return sanitized.trim();
  } catch (error) {
    logWarn('Error during attribute sanitization', error);
    return '';
  }
}

/**
 * Sanitize a URL to ensure it's safe
 * Only allows http, https, mailto, and relative URLs
 * 
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if unsafe
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const trimmedUrl = url.trim();
    
    // Allow relative URLs
    if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../')) {
      return trimmedUrl;
    }
    
    // Check if URL has a safe protocol
    const safeProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    const hasValidProtocol = safeProtocols.some(protocol => 
      trimmedUrl.toLowerCase().startsWith(protocol)
    );
    
    if (!hasValidProtocol) {
      logWarn('Unsafe URL protocol blocked', { url: trimmedUrl.substring(0, 50) });
      return '';
    }
    
    // Additional sanitization through DOMPurify
    const sanitized = DOMPurify.sanitize(trimmedUrl, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
    
    return sanitized;
  } catch (error) {
    logWarn('Error during URL sanitization', error);
    return '';
  }
}

/**
 * Sanitize plain text (removes all HTML tags)
 * Use this when you need to display user input as plain text
 * 
 * @param text - The text to sanitize
 * @returns Plain text with all HTML removed
 */
export function sanitizePlainText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  try {
    return DOMPurify.sanitize(text, {
      ALLOWED_TAGS: [],
      KEEP_CONTENT: true,
    });
  } catch (error) {
    logWarn('Error during plain text sanitization', error);
    return '';
  }
}

/**
 * Check if HTML content contains potentially dangerous elements
 * Useful for validation before processing
 * 
 * @param html - The HTML to check
 * @returns true if content appears safe, false if dangerous elements detected
 */
export function isHtmlSafe(html: string): boolean {
  if (!html || typeof html !== 'string') {
    return true;
  }

  try {
    const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG);
    // If sanitization changed the content significantly, it's potentially unsafe
    return Math.abs(html.length - sanitized.length) < 10;
  } catch (error) {
    logWarn('Error checking HTML safety', error);
    return false;
  }
}

/**
 * Configure DOMPurify hooks for additional security
 * This runs automatically when the module is imported
 */
function configureDOMPurify(): void {
  // Hook to enforce target="_blank" and rel="noopener noreferrer" on external links
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Set all links to open in new tab with security attributes
    if ('target' in node) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    
    // Remove any data URIs from src attributes (potential XSS vector)
    if (node.hasAttribute('src') && node.getAttribute('src')?.startsWith('data:')) {
      node.removeAttribute('src');
      logWarn('Data URI removed from src attribute (potential XSS)');
    }
  });
  
  logDebug('DOMPurify configured with security hooks');
}

// Configure DOMPurify on module load
configureDOMPurify();

// Export DOMPurify instance for advanced usage
export { DOMPurify };


